/**
 * Enterprise Navigation Overhaul — resolves a registry item's
 * `badgeSource` key to a real count, backed by an already-existing
 * query (never a new backend endpoint, per the plan's Decision 2).
 * `inventory-near-expiry` uses the real, dedicated `GET
 * .../stock-batches/expiring` endpoint. `procurement-pending-approvals`
 * has no dedicated count endpoint, so it counts `PENDING_APPROVAL`
 * purchase orders out of the first page — a disclosed approximation
 * (real hospital PO-approval queues are small; this undercounts only if
 * pending POs ever exceed one page), not a fabricated number.
 */
import { useQuery } from '@tanstack/react-query';
import { listExpiringStockBatches } from '@features/pharmacy-procurement/services/stock-batch.service';
import { listPurchaseOrders } from '@features/pharmacy-procurement/services/purchase-order.service';

const badgeProviders: Record<string, () => Promise<number>> = {
  'inventory-near-expiry': async () => (await listExpiringStockBatches(30)).length,
  'procurement-pending-approvals': async () => {
    const result = await listPurchaseOrders(1, 100);
    return result.items.filter((po) => po.status === 'PENDING_APPROVAL').length;
  },
};

export function useNavBadge(badgeSource: string | undefined): number | undefined {
  const { data } = useQuery({
    queryKey: ['nav-badge', badgeSource],
    queryFn: () => badgeProviders[badgeSource!]!(),
    enabled: Boolean(badgeSource && badgeProviders[badgeSource]),
    staleTime: 5 * 60_000,
  });
  return data && data > 0 ? data : undefined;
}
